import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { canUseStaffPage } from "@/lib/page-permissions";

const POLL_MS = 3000;
const DEFAULT_RESET_SECONDS = 30;

export default function KioskPage() {
  const { user } = useAuth();
  const allowed = canUseStaffPage(user, "kiosk");
  const [kioskState, setKioskState] = useState(null);
  const [displayData, setDisplayData] = useState(null);
  const [countdown, setCountdown] = useState(DEFAULT_RESET_SECONDS);

  useEffect(() => {
    // Session-local timers and liveness checks prevent late responses from
    // restoring client information after access is revoked or this page closes.
    let active = true;
    let denied = false;
    let busy = false;
    let currentClient = null;
    let pollTimer;
    let countdownTimer;
    let resetSeconds = DEFAULT_RESET_SECONDS;
    const live = () => active && !denied;
    const clearDisplay = () => {
      clearInterval(countdownTimer);
      currentClient = null;
      setKioskState(null);
      setDisplayData(null);
      setCountdown(resetSeconds);
    };
    const handleError = (error) => {
      if (!live()) return;
      clearDisplay();
      if ([401, 403].includes(error?.response?.status)) {
        denied = true;
        clearInterval(pollTimer);
      }
    };
    const startCountdown = () => {
      clearInterval(countdownTimer);
      let remaining = resetSeconds;
      setCountdown(remaining);
      countdownTimer = setInterval(() => {
        if (!live()) return;
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) {
          clearDisplay();
          api.put("/kiosk", { client_id: null, visible: false }).catch(handleError);
        }
      }, 1000);
    };
    async function poll() {
      if (!live() || busy) return;
      busy = true;
      try {
        const { data } = await api.get("/kiosk");
        if (!live()) return;
        if (data.client_id && data.visible) {
          if (data.client_id !== currentClient) {
            clearDisplay();
            const { data: client } = await api.get(`/clients/${data.client_id}/display`);
            if (!live()) return;
            currentClient = data.client_id;
            setDisplayData(client);
            startCountdown();
          }
          setKioskState(data);
        } else {
          clearDisplay();
        }
      } catch (error) {
        handleError(error);
      } finally {
        busy = false;
      }
    }
    async function init() {
      try {
        const { data: settings } = await api.get("/checkin-settings");
        if (!live()) return;
        const secs = settings.kiosk_reset_seconds;
        if (typeof secs === "number" && secs >= 10 && secs <= 300) {
          resetSeconds = secs;
          setCountdown(secs);
        }
      } catch (error) {
        handleError(error);
      }
      if (!live()) return;
      pollTimer = setInterval(poll, POLL_MS);
      poll();
    }
    clearDisplay();
    if (allowed) init();
    return () => {
      active = false;
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
    };
  }, [allowed, user?.id]);

  const showClient = allowed && kioskState?.visible && kioskState?.client_id && displayData;

  return (
    <div
      data-testid="kiosk-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: showClient ? "#F7F9F7" : "#052F66",
        transition: "background 0.4s ease",
      }}
    >
      {showClient ? (
        <ClientDisplay data={displayData} countdown={countdown} />
      ) : (
        <WaitingScreen hidden={kioskState?.client_id && !kioskState?.visible} />
      )}
    </div>
  );
}

function WaitingScreen({ hidden }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "2.5rem",
        padding: "3rem",
        textAlign: "center",
      }}
    >
      <Logo size={140} style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.35)" }} />

      <div>
        <h1
          style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "clamp(2.5rem, 6vw, 4rem)",
            fontWeight: 700,
            color: "#ffffff",
            marginBottom: "0.75rem",
            lineHeight: 1.1,
          }}
        >
          Welcome
        </h1>
        <p
          style={{
            fontSize: "clamp(1.1rem, 2.5vw, 1.5rem)",
            color: "rgba(255,255,255,0.65)",
            fontWeight: 400,
          }}
        >
          Pullman Child Welfare Food Bank
        </p>
      </div>

      {hidden && (
        <div
          style={{
            marginTop: "1rem",
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "1rem",
            padding: "0.75rem 1.5rem",
            color: "rgba(255,255,255,0.5)",
            fontSize: "0.85rem",
            fontWeight: 500,
          }}
        >
          Display paused by staff
        </div>
      )}

      {/* Decorative food bank illustration */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, overflow: "hidden", height: 160, opacity: 0.06 }}>
        <svg viewBox="0 0 1200 160" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
          <path d="M0,80 C200,20 400,140 600,80 C800,20 1000,140 1200,80 L1200,160 L0,160 Z" fill="white" />
        </svg>
      </div>
    </div>
  );
}

const LABEL_STYLE = {
  fontSize: "clamp(0.75rem, 1.5vw, 0.9rem)",
  textTransform: "uppercase",
  letterSpacing: "0.2em",
  color: "#8B918B",
  fontWeight: 600,
  marginBottom: "0.5rem",
};

function formatPhone(raw) {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1") return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return raw;
}

function calcAge(birthYear) {
  if (!birthYear) return null;
  const age = new Date().getFullYear() - birthYear;
  return age >= 0 && age < 130 ? age : null;
}

function ClientDisplay({ data, countdown }) {
  const address = data.address || "";
  const phone = formatPhone(data.phone);
  const email = data.email || "";
  const dietary = data.dietary_restrictions || [];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: "clamp(2rem, 5vw, 5rem)",
        gap: "2rem",
        maxWidth: 1100,
        margin: "0 auto",
        width: "100%",
      }}
    >
      {/* Header bar */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", borderBottom: "2px solid #E8E8E4", paddingBottom: "1.5rem" }}>
        <Logo size={48} />
        <span style={{ fontSize: "1rem", color: "#8B918B", fontWeight: 500 }}>
          Pullman Child Welfare Food Bank — Please verify your information below
        </span>
        <div
          data-testid="kiosk-countdown"
          style={{
            marginLeft: "auto",
            fontSize: "0.85rem",
            fontWeight: 500,
            color: countdown <= 10 ? "#B45309" : "#8B918B",
            background: countdown <= 10 ? "#FFF7ED" : "transparent",
            border: countdown <= 10 ? "1px solid #FED7AA" : "1px solid transparent",
            borderRadius: "999px",
            padding: "0.25rem 0.75rem",
            transition: "color 0.3s, background 0.3s, border-color 0.3s",
          }}
        >
          Resetting in {countdown}s…
        </div>
      </div>

      {/* Client name */}
      <div>
        <div style={LABEL_STYLE}>Client Name</div>
        <h1
          style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "clamp(2.5rem, 7vw, 5rem)",
            fontWeight: 700,
            color: "#052F66",
            lineHeight: 1.05,
          }}
          data-testid="kiosk-client-name"
        >
          {data.first_name} {data.last_name}
        </h1>
      </div>

      {/* Address */}
      {address && (
        <div>
          <div style={LABEL_STYLE}>Address on File</div>
          <p
            style={{ fontSize: "clamp(1.25rem, 3vw, 2rem)", color: "#2D3B2D", fontWeight: 400, lineHeight: 1.4 }}
            data-testid="kiosk-client-address"
          >
            {address}
          </p>
        </div>
      )}

      {/* Contact info */}
      {(phone || email) && (
        <div>
          <div style={LABEL_STYLE}>Contact on File</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }} data-testid="kiosk-contact">
            {phone && (
              <span style={{ fontSize: "clamp(1rem, 2.5vw, 1.5rem)", color: "#2D3B2D", fontWeight: 400 }}>
                {phone}
              </span>
            )}
            {email && (
              <span style={{ fontSize: "clamp(1rem, 2.5vw, 1.5rem)", color: "#2D3B2D", fontWeight: 400 }}>
                {email}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Dietary restrictions */}
      {dietary.length > 0 && (
        <div>
          <div style={LABEL_STYLE}>Dietary Restrictions</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem" }} data-testid="kiosk-dietary">
            {dietary.map((r) => (
              <span
                key={r}
                style={{
                  background: "#FFF7ED",
                  border: "1.5px solid #FED7AA",
                  borderRadius: "999px",
                  padding: "0.35rem 1rem",
                  fontSize: "clamp(0.85rem, 1.8vw, 1.1rem)",
                  fontWeight: 600,
                  color: "#92400E",
                }}
              >
                {r}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Household members */}
      {data.members?.length > 0 && (
        <div>
          <div style={{ ...LABEL_STYLE, marginBottom: "1rem" }}>
            Household Members ({data.household_size} total including primary)
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}
            data-testid="kiosk-members"
          >
            {data.members.map((m) => {
              const age = calcAge(m.birth_year);
              return (
                <div
                  key={m.id}
                  style={{ background: "#ffffff", border: "1.5px solid #E0E8E0", borderRadius: "1rem", padding: "1.25rem 1.5rem" }}
                >
                  <div style={{ fontSize: "clamp(1rem, 2vw, 1.4rem)", fontWeight: 600, color: "#1A2E1A", lineHeight: 1.2 }}>
                    {m.first_name} {m.last_name}
                  </div>
                  <div style={{ fontSize: "clamp(0.8rem, 1.5vw, 1rem)", color: "#8B918B", marginTop: "0.25rem" }}>
                    {m.relationship}
                    {age !== null ? ` · Age ${age}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer note */}
      <div style={{ marginTop: "auto", paddingTop: "1.5rem", borderTop: "1px solid #E8E8E4" }}>
        <p style={{ fontSize: "clamp(0.85rem, 1.5vw, 1rem)", color: "#8B918B", textAlign: "center" }}>
          If anything looks incorrect, please let our staff know so we can update your record.
        </p>
      </div>
    </div>
  );
}
